import React, { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ProgramSelect from "@/components/programs/ProgramSelect";

const emptyForm = {
  ownerName: "",
  phone: "",
  email: "",
  address: "",
  dogName: "",
  breed: "",
  age: "",
  goals: "",
  behaviorNotes: "",
  packageType: "",
  programPrice: 0,
  programSessions: 0,
  status: "Lead",
  nextSession: "",
  balance: 0,
  totalPaid: 0,
};

export default function ClientFormModal({ editingClient, onClose, onSubmit }) {
  const [form, setForm] = useState(() =>
    editingClient ? { ...emptyForm, ...editingClient } : { ...emptyForm }
  );
  const [autoFilledFromProgram, setAutoFilledFromProgram] = useState(null);

  function handleProgramSelected(program) {
    if (!program) {
      setAutoFilledFromProgram(null);
      return;
    }
    // Auto-fill program-level numbers + open balance (only when balance is
    // unset/empty — never overwrite a balance the trainer has already adjusted).
    setForm((prev) => {
      const next = { ...prev };
      // programPrice / programSessions: always pre-fill so trainer sees the
      // template values, but they can edit immediately.
      next.programPrice = Number(program.defaultPrice || 0);
      next.programSessions = Number(program.defaultSessionCount || 0);
      // Open balance: only fill if it's currently 0 (don't clobber existing).
      if (!prev.balance || Number(prev.balance) === 0) {
        next.balance = Number(program.defaultPrice || 0);
      }
      return next;
    });
    setAutoFilledFromProgram(program.name);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.ownerName || !form.dogName) {
      toast.error("Owner Name and Dog Name are required");
      return;
    }
    await onSubmit(form);
  }

  const editing = !!editingClient;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-xl"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-2xl font-bold">{editing ? "Edit Client" : "Add New Client"}</h2>
          <Button data-testid="close-form-btn" variant="outline" onClick={onClose} className="rounded-2xl">
            <X size={18} />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          {[
            ["ownerName", "Owner Name *"],
            ["phone", "Phone"],
            ["email", "Email"],
            ["address", "City / Area"],
            ["dogName", "Dog Name *"],
            ["breed", "Breed"],
            ["age", "Age"],
          ].map(([field, label]) => (
            <label key={field} className="space-y-1 text-sm font-medium">
              {label}
              <input
                data-testid={`form-${field}`}
                value={form[field] || ""}
                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                className="w-full rounded-xl border p-3 font-normal"
              />
            </label>
          ))}
          <label className="space-y-1 text-sm font-medium">
            Training Program
            <ProgramSelect
              value={form.packageType}
              onChange={(v) => setForm({ ...form, packageType: v })}
              onSelectProgram={handleProgramSelected}
              testId="form-packageType"
            />
            {autoFilledFromProgram && (
              <p
                data-testid="form-program-autofill-note"
                className="text-xs text-amber-700"
              >
                Pre-filled from "{autoFilledFromProgram}" — edit any field below for this client only.
              </p>
            )}
          </label>
          <label className="space-y-1 text-sm font-medium">
            Program Price
            <input
              data-testid="form-program-price"
              type="number"
              min="0"
              step="0.01"
              value={form.programPrice ?? 0}
              onChange={(e) => setForm({ ...form, programPrice: Number(e.target.value) })}
              className="w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Sessions Agreed
            <input
              data-testid="form-program-sessions"
              type="number"
              min="0"
              step="1"
              value={form.programSessions ?? 0}
              onChange={(e) => setForm({ ...form, programSessions: parseInt(e.target.value || "0", 10) })}
              className="w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Status
            <select
              data-testid="form-status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full rounded-xl border p-3 font-normal"
            >
              <option>Lead</option>
              <option>Active</option>
              <option>Needs Follow-up</option>
              <option>Completed</option>
            </select>
          </label>
          <label className="space-y-1 text-sm font-medium">
            Next Session
            <input
              data-testid="form-next-session"
              type="date"
              value={form.nextSession || ""}
              onChange={(e) => setForm({ ...form, nextSession: e.target.value })}
              className="w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Open Balance
            <input
              data-testid="form-balance"
              type="number"
              value={form.balance || 0}
              onChange={(e) => setForm({ ...form, balance: Number(e.target.value) })}
              className="w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Total Paid
            <input
              data-testid="form-total-paid"
              type="number"
              value={form.totalPaid || 0}
              onChange={(e) => setForm({ ...form, totalPaid: Number(e.target.value) })}
              className="w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium md:col-span-2">
            Training Goals
            <textarea
              data-testid="form-goals"
              value={form.goals || ""}
              onChange={(e) => setForm({ ...form, goals: e.target.value })}
              className="min-h-24 w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium md:col-span-2">
            Behavior Notes
            <textarea
              data-testid="form-behavior-notes"
              value={form.behaviorNotes || ""}
              onChange={(e) => setForm({ ...form, behaviorNotes: e.target.value })}
              className="min-h-24 w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <div className="flex gap-3 md:col-span-2">
            <Button data-testid="save-client-btn" type="submit" className="flex-1 rounded-2xl py-5">
              Save Client
            </Button>
            <Button
              data-testid="cancel-form-btn"
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-2xl py-5"
            >
              Cancel
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
